import { useMemo, useState } from 'react';
import { deleteTranslation, getTranslations, putTranslations, type AdminTranslation } from '../api';
import { fmtDate } from '../format';
import { useApi } from '../useApi';

/**
 * Mini App UI strings per locale. Edits are buffered client-side and flushed
 * with one bulk PUT ("Save all changed"); the app reads them publicly from
 * GET /v1/i18n/:locale.
 */
export function Translations() {
  const [locale, setLocale] = useState('en');
  const [loadedLocale, setLoadedLocale] = useState('en');
  const { data, error, loading, reload } = useApi(() => getTranslations(loadedLocale), [loadedLocale]);

  const [search, setSearch] = useState('');
  /** key -> edited value (also holds brand-new keys not in `data` yet). */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const rows = useMemo(() => data?.data ?? [], [data]);
  const byKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows]);

  /** Server rows + pending new keys, search-filtered. */
  const visible: (AdminTranslation | { id: null; key: string; value: string })[] = useMemo(() => {
    const pendingNew = Object.keys(drafts)
      .filter((k) => !byKey.has(k))
      .map((k) => ({ id: null, key: k, value: drafts[k]! }) as { id: null; key: string; value: string });
    const all = [...rows, ...pendingNew];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) => r.key.toLowerCase().includes(q) || (drafts[r.key] ?? r.value).toLowerCase().includes(q),
    );
  }, [rows, drafts, byKey, search]);

  const changed = Object.entries(drafts).filter(([k, v]) => byKey.get(k)?.value !== v);

  const load = () => {
    const next = locale.trim();
    if (!next) return;
    setDrafts({});
    setActionError(null);
    if (next === loadedLocale) reload();
    else setLoadedLocale(next);
  };

  const addRow = () => {
    const key = newKey.trim();
    if (!key) return;
    setDrafts((d) => ({ ...d, [key]: newValue }));
    setNewKey('');
    setNewValue('');
  };

  const saveAll = async () => {
    if (changed.length === 0) return;
    setBusy(true);
    setActionError(null);
    try {
      await putTranslations(loadedLocale, Object.fromEntries(changed));
      setDrafts({});
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: AdminTranslation) => {
    if (!window.confirm(`Delete "${row.key}" (${loadedLocale})?`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteTranslation(row.id);
      setDrafts(({ [row.key]: _dropped, ...rest }) => rest);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Translations<span className="dot">.</span>
        </h1>
        {data && <span className="muted">{rows.length} strings · {loadedLocale}</span>}
      </div>
      <p className="hint" style={{ marginBottom: 16 }}>
        UI strings the Mini App fetches from <code>GET /v1/i18n/:locale</code> (public, ~1 min cache). Edit inline, then
        “Save all changed”.
      </p>
      {actionError && <p className="error-line">{actionError}</p>}

      <div className="card">
        <div className="card-head">
          <span className="card-title">Locale</span>
        </div>
        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ width: 200 }}>
            <label>Locale</label>
            <input
              className="input"
              placeholder="en, fa, en-US…"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
            />
          </div>
          <button className="btn" disabled={loading || !locale.trim()} onClick={load}>
            Load
          </button>
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Filter</label>
            <input
              className="input"
              placeholder="Filter keys/values…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" disabled={busy || changed.length === 0} onClick={() => void saveAll()}>
            Save all changed{changed.length > 0 ? ` (${changed.length})` : ''}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="card-title">Add row</span>
        </div>
        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ width: 240 }}>
            <label>New key</label>
            <input
              className="input"
              placeholder="premium.title"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Value</label>
            <input className="input" placeholder="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} />
          </div>
          <button className="btn" disabled={!newKey.trim()} onClick={addRow}>
            Add row
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {error ? (
          <div className="empty">{error}</div>
        ) : loading && !data ? (
          <div className="empty">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="empty">
            {rows.length === 0
              ? `No strings for "${loadedLocale}" yet — add a row above.`
              : 'No rows match the filter.'}
          </div>
        ) : (
          <table className="flm">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value ({loadedLocale})</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const draft = drafts[r.key];
                const isDirty = draft !== undefined && (r.id === null || draft !== r.value);
                return (
                  <tr key={r.id ?? `new:${r.key}`}>
                    <td className="mono">
                      {r.key}
                      {isDirty && <span className="dot"> *</span>}
                    </td>
                    <td>
                      <input
                        className="input"
                        style={{ width: '100%', minWidth: 240 }}
                        value={draft ?? r.value}
                        onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
                      />
                    </td>
                    <td className="muted">{r.id ? fmtDate((r as AdminTranslation).updatedAt) : 'unsaved'}</td>
                    <td>
                      <div className="row-actions">
                        {r.id ? (
                          <button className="btn btn-danger" disabled={busy} onClick={() => void remove(r as AdminTranslation)}>
                            Delete
                          </button>
                        ) : (
                          <button
                            className="btn"
                            disabled={busy}
                            onClick={() => setDrafts(({ [r.key]: _dropped, ...rest }) => rest)}
                          >
                            Discard
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
